import React from 'react';
import { Users, TrendingUp, Lightbulb, Target, Linkedin } from 'lucide-react';
import useInView from '../hooks/useInView';

const AboutSection = () => {
  const [headerRef, headerInView] = useInView();
  const [storyRef, storyInView] = useInView();
  const [mvRef, mvInView] = useInView();
  const [leadershipRef, leadershipInView] = useInView();
  const [valuesRef, valuesInView] = useInView();

  return (
    <section id="about" style={{
      background: 'var(--bg-primary)',
      padding: '100px 7.6923%',
      position: 'relative'
    }}>
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto'
      }}>
        {/* Section Header */}
        <div
          ref={headerRef}
          className={`reveal-root ${headerInView ? 'is-in-view' : ''}`}
          style={{ textAlign: 'center', marginBottom: '60px' }}
        >
          <div className="reveal" style={{ '--i': 0,
            display: 'inline-block',
            padding: '8px 16px',
            background: 'rgba(0, 255, 209, 0.1)',
            border: '1px solid var(--brand-primary)',
            marginBottom: '20px',
            fontSize: '14px',
            color: 'var(--brand-primary)',
            fontWeight: '500'
          }}>
            ABOUT VDM
          </div>
          <h2 className="display-large reveal" style={{ '--i': 1, marginBottom: '20px' }}>
            Driving <span style={{ color: 'var(--brand-primary)' }}>Innovation</span> for Tomorrow
          </h2>
          <p className="body-medium reveal" style={{ '--i': 2, maxWidth: '800px', margin: '0 auto' }}>
            VDM Business Innovations is where industry leaders unite to form a knowledge-driven innovation hub,
            evolving with technological advancements to drive industry transformation.
          </p>
        </div>

        {/* About Content Grid */}
        <div
          ref={storyRef}
          className={`reveal-root about-grid ${storyInView ? 'is-in-view' : ''}`}
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '60px',
            marginBottom: '60px'
          }}
        >
          {/* Left - Image */}
          <div className="reveal about-grid-child" style={{
            '--i': 0,
            borderRadius: '0px',
            overflow: 'hidden',
            height: '400px'
          }}>
            <img 
              src="https://images.unsplash.com/photo-1600880292089-90a7e086ee0c" 
              alt="Team collaboration" 
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
          </div>

          {/* Right - Content */}
          <div className="reveal" style={{ '--i': 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <h3 className="heading-1" style={{ marginBottom: '20px' }}>
              Our Story
            </h3>
            <p className="body-medium" style={{ marginBottom: '30px' }}>
              Born from the collective expertise of leaders across Hospitality, Commercial Real Estate, and Technology sectors, VDM Business Innovations represents a paradigm shift in business consultancy.
            </p>
            <p className="body-medium" style={{ marginBottom: '30px' }}>
              We don't just solve today's problems — we architect solutions that evolve with your business, leveraging cutting-edge technology to simplify complex operations and drive sustainable growth.
            </p>
            <div style={{
              padding: '24px',
              background: 'rgba(255, 255, 255, 0.05)',
              borderLeft: '4px solid var(--brand-primary)'
            }}>
              <p className="body-medium" style={{ fontStyle: 'italic' }}>
                "Innovating for Tomorrow — Enabling Businesses to Thrive through Smart Technology."
              </p>
            </div>
          </div>
        </div>

        {/* Mission and Vision */}
        <div
          ref={mvRef}
          className={`reveal-root mission-vision-grid ${mvInView ? 'is-in-view' : ''}`}
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '60px',
            marginBottom: '60px'
          }}
        >
          {/* Mission */}
          <div className="reveal" style={{
            '--i': 0,
            padding: '60px',
            background: 'rgba(0, 255, 209, 0.05)',
            border: '2px solid var(--brand-primary)'
          }}>
            <h3 className="heading-1" style={{ marginBottom: '24px', color: 'var(--brand-primary)' }}>
              Our Mission
            </h3>
            <p className="body-medium" style={{ lineHeight: '1.8' }}>
              To elevate industry standards with integrity and deep expertise; to foster technology and sustainability while inspiring responsible innovation.
            </p>
          </div>

          {/* Vision */}
          <div style={{
            padding: '60px',
            background: 'rgba(255, 255, 255, 0.03)',
            border: '1px solid var(--border-subtle)'
          }}>
            <h3 className="heading-1" style={{ marginBottom: '24px', color: 'var(--brand-primary)' }}>
              Our Vision
            </h3>
            <p className="body-medium" style={{ lineHeight: '1.8' }}>
              A world where business transformation meets human values, empowering brands, people, and society through bold, adaptable solutions.
            </p>
          </div>
        </div>

        {/* Founder & CEO Section */}
        <div
          ref={leadershipRef}
          data-leadership-wrap="true"
          className={`reveal-root ${leadershipInView ? 'is-in-view' : ''}`}
          style={{
            marginTop: '80px',
            marginBottom: '60px',
            padding: '80px',
            background: 'rgba(0, 255, 209, 0.05)',
            border: '2px solid var(--brand-primary)'
          }}
        >
          <div className="reveal" style={{ '--i': 0, textAlign: 'center', marginBottom: '60px' }}>
            <div style={{
              display: 'inline-block',
              padding: '8px 16px',
              background: 'rgba(0, 255, 209, 0.1)',
              border: '1px solid var(--brand-primary)',
              marginBottom: '20px',
              fontSize: '14px',
              color: 'var(--brand-primary)',
              fontWeight: '500'
            }}>
              LEADERSHIP
            </div>
            <h2 className="display-medium" style={{ color: 'var(--brand-primary)' }}>
              Meet Our Leadership
            </h2>
            <p className="body-medium" style={{ maxWidth: '760px', margin: '20px auto 0', color: 'var(--text-secondary)' }}>
              The minds steering VDM Business Innovations &mdash; combining decades of operational depth with brand and customer leadership.
            </p>
          </div>

          {/* === Leaders Side-by-Side === */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '40px',
            alignItems: 'start'
          }} className="leaders-grid">

            {/* === Founder & CEO === */}
            <div data-testid="founder-ceo-card" className="reveal" style={{
              '--i': 1,
              padding: '40px',
              background: 'rgba(255, 255, 255, 0.03)',
              border: '1px solid var(--border-subtle)',
              display: 'flex',
              flexDirection: 'column',
              transition: 'all 0.4s ease-in-out'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--brand-primary)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border-subtle)'; }}
            >
              {/* Photo */}
              <div style={{
                overflow: 'hidden',
                border: '2px solid var(--brand-primary)',
                marginBottom: '28px',
                aspectRatio: '4 / 5'
              }}>
                <img
                  src="https://customer-assets.emergentagent.com/job_biz-innovations/artifacts/ell0z8bd_image.png"
                  alt="Sandeep Shankar Devadiga"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              </div>

              <h3 className="heading-1" style={{ marginBottom: '6px', fontSize: '28px' }}>
                Sandeep Shankar Devadiga
              </h3>
              <p style={{
                fontSize: '17px',
                color: 'var(--brand-primary)',
                fontWeight: '600',
                marginBottom: '20px'
              }}>
                Founder & CEO
              </p>

              <a
                href="https://www.linkedin.com/in/sandeep31051976"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '9px 18px',
                  background: 'rgba(0, 119, 181, 0.1)',
                  border: '1px solid #0077b5',
                  color: '#0077b5',
                  textDecoration: 'none',
                  fontSize: '13px',
                  fontWeight: '500',
                  marginBottom: '26px',
                  alignSelf: 'flex-start',
                  transition: 'all 0.3s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#0077b5';
                  e.currentTarget.style.color = '#ffffff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(0, 119, 181, 0.1)';
                  e.currentTarget.style.color = '#0077b5';
                }}
              >
                <Linkedin size={16} />
                Connect on LinkedIn
              </a>

              <p className="body-medium" style={{ marginBottom: '18px', lineHeight: '1.8' }}>
                A visionary who combined experience, risk, and real-world insight to create VDM Business Innovations, a company born of ambition, support, and the desire to do things differently.
              </p>

              <p className="body-medium" style={{ marginBottom: '18px', lineHeight: '1.8' }}>
                With over <strong>29 years in corporate leadership</strong>, Sandeep Devadiga has been on both sides of the table&mdash;as a corporate professional and as a solution creator.
              </p>

              <p className="body-medium" style={{ marginBottom: '18px', lineHeight: '1.8' }}>
                Having witnessed pain points across facility, compliance, and operations firsthand, he founded VDM Business Innovations to engineer real, field-tested solutions that make daily work lives simpler, sustainable, and efficient.
              </p>

              <p className="body-medium" style={{ marginBottom: '28px', lineHeight: '1.8' }}>
                His journey reflects resilience, purpose, and a passion for driving transformation through innovation.
              </p>

              <div style={{
                padding: '24px',
                background: 'rgba(255, 255, 255, 0.05)',
                borderLeft: '4px solid var(--brand-primary)',
                marginTop: 'auto'
              }}>
                <p style={{
                  fontSize: '16px',
                  fontStyle: 'italic',
                  lineHeight: '1.8',
                  color: 'var(--text-primary)'
                }}>
                  "Entrepreneurship is my leap of faith. VDM is where I dared to make dreams real and invite others to build a brighter future together."
                </p>
                <p style={{
                  marginTop: '14px',
                  fontSize: '13px',
                  color: 'var(--brand-primary)',
                  fontWeight: '600'
                }}>
                  &mdash; Sandeep Shankar Devadiga
                </p>
              </div>
            </div>

            {/* === Co-Founder & CMO === */}
            <div data-testid="cofounder-cmo-card" className="reveal" style={{
              '--i': 2,
              padding: '40px',
              background: 'rgba(255, 255, 255, 0.03)',
              border: '1px solid var(--border-subtle)',
              display: 'flex',
              flexDirection: 'column',
              transition: 'all 0.4s ease-in-out'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--brand-primary)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border-subtle)'; }}
            >
              {/* Photo */}
              <div style={{
                overflow: 'hidden',
                border: '2px solid var(--brand-primary)',
                marginBottom: '28px',
                aspectRatio: '4 / 5'
              }} data-testid="cofounder-photo">
                <img
                  src="https://customer-assets-eiarnc6j.emergentagent.net/job_fd78a43a-475d-4d4e-ba79-642a920f6f2b/artifacts/yo9xlk5l_Vanita%20Devadiga%20photo.jpeg"
                  alt="Vanita Devadiga"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center top'
                  }}
                />
              </div>

              <h3 className="heading-1" style={{ marginBottom: '6px', fontSize: '28px' }}>
                Vanita Devadiga
              </h3>
              <p style={{
                fontSize: '17px',
                color: 'var(--brand-primary)',
                fontWeight: '600',
                marginBottom: '20px'
              }}>
                Co-Founder & Chief Marketing Officer
              </p>

              <a
                href="https://www.linkedin.com/in/vanita-devadiga-4b688236/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cofounder-linkedin-link"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '9px 18px',
                  background: 'rgba(0, 119, 181, 0.1)',
                  border: '1px solid #0077b5',
                  color: '#0077b5',
                  textDecoration: 'none',
                  fontSize: '13px',
                  fontWeight: '500',
                  marginBottom: '26px',
                  alignSelf: 'flex-start',
                  transition: 'all 0.3s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#0077b5';
                  e.currentTarget.style.color = '#ffffff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(0, 119, 181, 0.1)';
                  e.currentTarget.style.color = '#0077b5';
                }}
              >
                <Linkedin size={16} />
                Connect on LinkedIn
              </a>

              <p className="body-medium" style={{ marginBottom: '18px', lineHeight: '1.8' }}>
                As Co-Founder and Chief Marketing Officer of VDM Business Innovations, Vanita Devadiga leads brand strategy, customer experience, and market positioning across the entire VDM portfolio.
              </p>

              <p className="body-medium" style={{ marginBottom: '18px', lineHeight: '1.8' }}>
                With a deep grounding in <strong>marketing leadership, brand building, and customer engagement</strong>, she shapes how VDM&apos;s solutions reach, resonate with, and earn the trust of the businesses and communities they serve.
              </p>

              <p className="body-medium" style={{ marginBottom: '28px', lineHeight: '1.8' }}>
                Her approach blends marketing craft with operational empathy, ensuring that growth at VDM is always paired with relevance, reach, and lasting customer relationships.
              </p>

              <div style={{
                padding: '24px',
                background: 'rgba(255, 255, 255, 0.05)',
                borderLeft: '4px solid var(--brand-primary)',
                marginTop: 'auto'
              }}>
                <p style={{
                  fontSize: '16px',
                  fontStyle: 'italic',
                  lineHeight: '1.8',
                  color: 'var(--text-primary)'
                }}>
                  "Great brands aren&apos;t built by marketing alone &mdash; they&apos;re built when every promise we make is matched by the experience we deliver."
                </p>
                <p style={{
                  marginTop: '14px',
                  fontSize: '13px',
                  color: 'var(--brand-primary)',
                  fontWeight: '600'
                }}>
                  &mdash; Vanita Devadiga
                </p>
              </div>
            </div>

          </div>
        </div>

        {/* Core Values */}
        <div
          ref={valuesRef}
          className={`reveal-root ${valuesInView ? 'is-in-view' : ''}`}
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '30px',
            marginTop: '60px'
          }}
        >
          <div className="reveal" style={{
            '--i': 0,
            padding: '40px',
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid var(--border-subtle)',
            transition: 'border-color 0.4s ease-in-out, transform 0.4s ease-in-out'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = 'var(--brand-primary)';
            e.currentTarget.style.transform = 'translateY(-8px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = 'var(--border-subtle)';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
          >
            <Lightbulb size={40} color="var(--brand-primary)" style={{ marginBottom: '20px' }} />
            <h4 className="heading-3" style={{ marginBottom: '12px' }}>Innovation-Driven</h4>
            <p className="body-small">Leveraging next-generation technology to create adaptive, future-ready solutions.</p>
          </div>

          <div className="reveal" style={{
            '--i': 1,
            padding: '40px',
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid var(--border-subtle)',
            transition: 'border-color 0.4s ease-in-out, transform 0.4s ease-in-out'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = 'var(--brand-primary)';
            e.currentTarget.style.transform = 'translateY(-8px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = 'var(--border-subtle)';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
          >
            <Users size={40} color="var(--brand-primary)" style={{ marginBottom: '20px' }} />
            <h4 className="heading-3" style={{ marginBottom: '12px' }}>Customer-Centric</h4>
            <p className="body-small">Personalized consulting and implementation support tailored to your unique needs.</p>
          </div>

          <div className="reveal" style={{
            '--i': 2,
            padding: '40px',
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid var(--border-subtle)',
            transition: 'border-color 0.4s ease-in-out, transform 0.4s ease-in-out'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = 'var(--brand-primary)';
            e.currentTarget.style.transform = 'translateY(-8px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = 'var(--border-subtle)';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
          >
            <TrendingUp size={40} color="var(--brand-primary)" style={{ marginBottom: '20px' }} />
            <h4 className="heading-3" style={{ marginBottom: '12px' }}>Growth-Focused</h4>
            <p className="body-small">Strategic solutions designed to scale with your business and drive measurable results.</p>
          </div>

          <div className="reveal" style={{
            '--i': 3,
            padding: '40px',
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid var(--border-subtle)',
            transition: 'border-color 0.4s ease-in-out, transform 0.4s ease-in-out'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = 'var(--brand-primary)';
            e.currentTarget.style.transform = 'translateY(-8px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = 'var(--border-subtle)';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
          >
            <Target size={40} color="var(--brand-primary)" style={{ marginBottom: '20px' }} />
            <h4 className="heading-3" style={{ marginBottom: '12px' }}>Industry Expertise</h4>
            <p className="body-small">Deep cross-industry insights from proven leaders in hospitality, CRE, and technology.</p>
          </div>
        </div>
      </div>

      <style>{`
        /* Tablet: stack the founder cards below 1024px so they don't squish */
        @media (max-width: 1024px) {
          .leaders-grid {
            grid-template-columns: 1fr !important;
            gap: 32px !important;
          }
        }

        /* Mobile: tighter padding + single-column everything */
        @media (max-width: 768px) {
          .about-grid {
            grid-template-columns: 1fr !important;
            gap: 40px !important;
          }
          .mission-vision-grid {
            grid-template-columns: 1fr !important;
            gap: 24px !important;
          }
          .mission-vision-grid > div {
            padding: 32px !important;
          }
          .founder-grid {
            grid-template-columns: 1fr !important;
            gap: 40px !important;
          }
          .leaders-grid {
            gap: 28px !important;
          }
          /* Leadership container padding */
          [data-leadership-wrap="true"] {
            padding: 32px 20px !important;
            margin-top: 40px !important;
          }
          /* Founder / Co-Founder cards */
          [data-testid="founder-ceo-card"],
          [data-testid="cofounder-cmo-card"] {
            padding: 24px !important;
          }
          /* Founder headings */
          [data-testid="founder-ceo-card"] h3,
          [data-testid="cofounder-cmo-card"] h3 {
            font-size: 22px !important;
            line-height: 1.25 !important;
          }
        }
      `}</style>
    </section>
  );
};

export default AboutSection;